import { ChangeEventHandler, KeyboardEventHandler, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Button, IconButton, TextField, Typography } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from '@mui/icons-material';

type TablePaginationProps = {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
};

const TablePagination = ({ currentPage, pageSize, totalCount, onPageChange }: TablePaginationProps) => {
  const numOfPages = Math.ceil(totalCount / pageSize);
  const isPreviousDisabled = !currentPage || currentPage <= 1;
  const isNextDisabled = !currentPage || currentPage >= numOfPages;
  const [localPage, setLocalPage] = useState(currentPage.toString());

  const handlePageInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const newValue = e.target.value;

    /**
     * ONLY allows input below:
     * 1. empty input
     * 2. numbers from 0 to 9
     * 3. numbers greater than 0
     * 4. numbers less than or equal to the total number of pages
     */
    if (newValue === '' || (/^$|^[0-9]+$/.test(newValue) && +newValue > 0 && +newValue <= numOfPages)) {
      setLocalPage(e.target.value);
    }
  };

  const handlePageInputKeydown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    // listen to enter key and input must be a valid number
    if (e.key === 'Enter' && !isNaN(+localPage)) {
      onPageChange(+localPage);
    }
  };

  const handleFirstPageClick = () => {
    setLocalPage('1');
    onPageChange(1);
  };

  const handlePreviousClick = () => {
    const newPage = currentPage - 1;
    setLocalPage(newPage.toString());
    onPageChange(newPage);
  };

  const handleNextClick = () => {
    const newPage = currentPage + 1;
    setLocalPage(newPage.toString());
    onPageChange(newPage);
  };

  const handleLastPageClick = () => {
    setLocalPage(numOfPages.toString());
    onPageChange(numOfPages);
  };

  useEffect(() => {
    if (currentPage !== +localPage) setLocalPage(currentPage.toString());
  }, [currentPage]);

  return (
    <>
      <IconButton aria-label="first page" color="primary" disabled={isPreviousDisabled} onClick={handleFirstPageClick}>
        <KeyboardDoubleArrowLeft />
      </IconButton>
      <Button startIcon={<KeyboardArrowLeft />} disabled={isPreviousDisabled} onClick={handlePreviousClick}>
        Previous
      </Button>
      <StyledInput
        size="small"
        total={numOfPages}
        value={localPage}
        onChange={handlePageInputChange}
        onKeyDown={handlePageInputKeydown}
        type="number"
        disabled={numOfPages <= 1}
      />
      <Typography>of</Typography>
      <Typography>{numOfPages.toLocaleString()}</Typography>
      <Button endIcon={<KeyboardArrowRight />} disabled={isNextDisabled} onClick={handleNextClick}>
        Next
      </Button>
      <IconButton aria-label="last page" color="primary" disabled={isNextDisabled} onClick={handleLastPageClick}>
        <KeyboardDoubleArrowRight />
      </IconButton>
    </>
  );
};

export default TablePagination;

const StyledInput = styled(TextField)<{ total: number }>(({ total }) => ({
  minWidth: '50px',
  width: total.toString().length * 10 + 18 + 'px',
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0
  },
  '& input': {
    MozAppearance: 'textfield'
  }
}));
